import { CommonFilterModel, CommonParams, CommonExportType } from '@/api/resources/common/shared/models/common.models'
import { ReportGridRequest } from '@/api/services/common.dto'
import { StatisticalAnalysisIncidentsStatisticsDossierBaseRequest, StatisticalAnalysisIncidentsStatisticsDossierDetailViewChart } from '@/modules/statistical-analysis-module/services/incidents/incidents-dossier.dto'
import { StatisticalAnalysisSampleTagsStatisticsDossierBaseRequest, StatisticalAnalysisSampleTagsStatisticsDossierDetailExternalViewTable, StatisticalAnalysisSampleTagsStatisticsDossierDetailRequest, StatisticalAnalysisSampleTagsStatisticsDossierDetailViewChart, StatisticalAnalysisSampleTagsStatisticsDossierDetailViewChartResponseInternalGroups, StatisticalAnalysisSampleTagsStatisticsDossierDetailViewTable, StatisticalAnalysisSampleTagsStatisticsDossierGroupRequest, StatisticalAnalysisSampleTagsStatisticsDossierSamplesResponse } from '@/modules/statistical-analysis-module/services/sample-tags-dossier/sample-tags-dossier.dto'
import { LaboratoryStatisticalAnalysisSidebarDateInput } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-sidebar-date-and-centers/statistical-analysis-sidebar-date-and-centers.model'
import { StatisticalAnalysisSearchBySidebarComboItemCombo } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-sidebar-search-by/statistical-analysis-sidebar-search-by.model'
import { StatisticalAnalysisViewDetailChartData } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-view-detail-chart/statistical-analysis-view-detail-chart.model'
import { formatDatetimes, StatisticalAnalysisDynamicObject } from '@/modules/statistical-analysis-module/shared/models/shared.model'
import { StatisticalAnalysisSampleTagsDossierExternalInfo } from '@/modules/statistical-analysis-module/views/labeled-samples-dossier/labeled-samples-dossier.model'
import { Nullable } from '@/shared/types/app.types'
import axios, { AxiosResponse } from 'axios'
import { SeriesOptionsType } from 'highcharts'
import { chain, find, isNil, map } from 'lodash'
import { store } from '@/store/store'

class SampleTagsStatisticsDossierService {
    async getGeneralView(filters: StatisticalAnalysisSampleTagsDossierExternalInfo): Promise<StatisticalAnalysisSampleTagsStatisticsDossierSamplesResponse> {
        const baseRequest = this.getBaseRequest(filters)
        const params = {
            ...baseRequest
        }
        const response = await axios.get<StatisticalAnalysisSampleTagsStatisticsDossierSamplesResponse>(`/statisticAnalysis/dossiers/sampleTag`, { params })
        return response.data
    }

    async getDetailViewTable(
        filterModel: CommonFilterModel, params: CommonParams,
        sampleTagId: number, baseRequest: StatisticalAnalysisIncidentsStatisticsDossierBaseRequest,
        detailRequest: StatisticalAnalysisSampleTagsStatisticsDossierDetailRequest, scrollId: Nullable<string>): Promise<StatisticalAnalysisSampleTagsStatisticsDossierDetailViewTable> {
        let extraParams = {
            ...filterModel,
            ...params,
            ...baseRequest,
            ...detailRequest
        }
        if (scrollId) {
            extraParams = {
                ...extraParams,
                ...{ scrollId }
            }
        }
        const response = await axios.get<StatisticalAnalysisSampleTagsStatisticsDossierDetailViewTable>(`/statisticAnalysis/dossiers/sampleTag/${sampleTagId}/detail`, { params: extraParams })
        return response.data
    }

    async getExternalViewGeneralList(filterModel: CommonFilterModel, commonParams: CommonParams, sampleTagId: number, baseRequest: StatisticalAnalysisIncidentsStatisticsDossierBaseRequest, scrollId: Nullable<string>, totalElements: Nullable<number>): Promise<StatisticalAnalysisSampleTagsStatisticsDossierDetailExternalViewTable> {
        let params = {
            ...filterModel,
            ...commonParams,
            ...baseRequest
        }

        if (!isNil(totalElements) && totalElements > 0) {
            params = {
                ...params,
                ...{ totalElements }
            }
        }

        if (scrollId) {
            params = {
                ...params,
                ...{ scrollId }
            }
        }

        const response = await axios.get<StatisticalAnalysisSampleTagsStatisticsDossierDetailExternalViewTable>(`/statisticAnalysis/dossiers/sampleTag/${sampleTagId}/generalList`, { params })
        return response.data
    }

    async getExternalViewDetailList(filterModel: CommonFilterModel, commonParams: CommonParams, baseRequest: StatisticalAnalysisSampleTagsStatisticsDossierBaseRequest, groupingParams: StatisticalAnalysisDynamicObject, tagId: number, scrollId: Nullable<string>, totalElements: Nullable<number>): Promise<StatisticalAnalysisSampleTagsStatisticsDossierDetailExternalViewTable> {
        const bodyParams = {
            ...groupingParams
        }
        let params = {
            ...filterModel,
            ...commonParams,
            ...baseRequest
        }

        if (!isNil(totalElements) && totalElements > 0) {
            params = {
                ...params,
                ...{ totalElements }
            }
        }

        if (scrollId) {
            params = {
                ...params,
                ...{ scrollId }
            }
        }

        const response = await axios.put<StatisticalAnalysisSampleTagsStatisticsDossierDetailExternalViewTable>(`/statisticAnalysis/dossiers/sampleTag/${tagId}/detailList`, bodyParams, { params })
        return response.data
    }

    async getDetailViewChart(tagIg: number, baseRequest: StatisticalAnalysisIncidentsStatisticsDossierBaseRequest, groupRequest: StatisticalAnalysisSampleTagsStatisticsDossierGroupRequest): Promise<StatisticalAnalysisViewDetailChartData> {
        const params = {
            ...baseRequest,
            ...groupRequest
        }
        const response = await axios.get<StatisticalAnalysisIncidentsStatisticsDossierDetailViewChart[]>(`/statisticAnalysis/dossiers/sampleTag/${tagIg}/stats`, { params })
        return this._parseChartData(response.data)
    }

    public getBaseRequest(filter: StatisticalAnalysisSampleTagsDossierExternalInfo): StatisticalAnalysisSampleTagsStatisticsDossierBaseRequest {
        return {
            startDate: encodeURIComponent(formatDatetimes(filter.startDate as Date)),
            endDate: encodeURIComponent(formatDatetimes(filter.endDate as Date)),
            laboratoryIds: (filter.laboratories as LaboratoryStatisticalAnalysisSidebarDateInput[]).map((item: LaboratoryStatisticalAnalysisSidebarDateInput) => item.labId),
            grossedOrProcessed: filter.grossedProcessedSamples as boolean,
            groupByCase: filter.groupByCase as boolean,
            laboratoryUserId: store.getters.labId,
            stateIds: (filter.states as StatisticalAnalysisSearchBySidebarComboItemCombo[]).map((item: StatisticalAnalysisSearchBySidebarComboItemCombo) => item.id)
        }
    }

    public generalExportTo(externalFilter: StatisticalAnalysisSampleTagsDossierExternalInfo, bodyParams: ReportGridRequest, exportType: CommonExportType): Promise<AxiosResponse> {
        const params = {
            ...this.getBaseRequest(externalFilter),
            ...{ exportType }
        }
        return axios.put(`statisticAnalysis/dossiers/sampleTag/${externalFilter.trackingGeneralSelected?.key}/generalList/export`, bodyParams,
            { params, responseType: 'arraybuffer' }
        ).then(response => response)
    }

    public detailExportTo(
        externalFilter: StatisticalAnalysisSampleTagsDossierExternalInfo,
        reportGridRequest: ReportGridRequest,
        groupByFields: string [],
        groupValueFilterRequests: StatisticalAnalysisDynamicObject[],
        tagId: number,
        exportType: CommonExportType
    ): Promise<AxiosResponse> {
        const params = {
            ...{ groupByFields },
            ...this.getBaseRequest(externalFilter),
            ...{ exportType }
        }

        const bodyParams = {
            gridRequest: {
                ...reportGridRequest
            },
            groupValueFilterRequests
        }

        return axios.put(`statisticAnalysis/dossiers/sampleTag/${tagId}/detail/export`, bodyParams,
            { params, responseType: 'arraybuffer' }
        ).then(response => response)
    }

    public detailExternalExportTo(
        externalFilter: StatisticalAnalysisSampleTagsDossierExternalInfo,
        reportGridRequest: ReportGridRequest,
        groupValueFilterRequest: StatisticalAnalysisDynamicObject,
        tagId: number,
        exportType: CommonExportType
    ): Promise<AxiosResponse> {
        const params = {
            ...this.getBaseRequest(externalFilter),
            ...{ exportType }
        }

        const bodyParams = {
            gridRequest: {
                ...reportGridRequest
            },
            groupValueFilterRequest
        }

        return axios.put(`statisticAnalysis/dossiers/sampleTag/${tagId}/detailList/export`, bodyParams,
            { params, responseType: 'arraybuffer' }
        ).then(response => response)
    }

    private _parseChartData(response: StatisticalAnalysisSampleTagsStatisticsDossierDetailViewChart[]): StatisticalAnalysisViewDetailChartData {
        const categories = map(response, 'groupBy1')
        const labelSeries = chain(response).map('groupBy2').flatMap().map('name').uniq().value()
        const parsedResponse = this._getDataSeriesChart(labelSeries, response)
        const series = chain(parsedResponse).map('groupBy2').flatMap().groupBy('name').mapValues((value: any, key: string) => {
            return {
                name: key,
                data: map(value, 'count')
            }
        }).flatMap().value() as SeriesOptionsType[]
        return {
            categories,
            series
        }
    }

    private _getDataSeriesChart(uniqLabels: string[], response:StatisticalAnalysisSampleTagsStatisticsDossierDetailViewChart[]): StatisticalAnalysisSampleTagsStatisticsDossierDetailViewChart[] {
        return response.map((groupResponse: StatisticalAnalysisSampleTagsStatisticsDossierDetailViewChart) => {
            groupResponse.groupBy2 = uniqLabels.map((uniqLabel: string) => {
                const foundGroupBy2Item = find(groupResponse.groupBy2, { 'name': uniqLabel })
                if (foundGroupBy2Item) {
                    return foundGroupBy2Item
                } else {
                    return {
                        name: uniqLabel,
                        count: null
                    }
                }
            }) as StatisticalAnalysisSampleTagsStatisticsDossierDetailViewChartResponseInternalGroups[]
            return groupResponse
        })
    }
}

export const sampleTagsStatisticsDossierService = new SampleTagsStatisticsDossierService()
